import {useDiffToggleStore} from '../../allspice_app/stores/diffToggle.ts';

// Re-export types and constants from the store for backward compatibility
export type {DiffToggleOption} from '../../allspice_app/stores/diffToggle.ts';
export {DIFF_TOGGLE_OPTIONS} from '../../allspice_app/stores/diffToggle.ts';

/**
 * Apply diff layer visibility to SVG elements within a container.
 * Handles diff_to/diff_from layers, backdrops, and color desaturation.
 *
 * @param container - The DOM element to query within (e.g. a .diff-file-box or document).
 *   Passing `document` is safe when only one diff file is visible at a time (Vue app context).
 *   In box.tmpl (multiple files on one page), callers should scope to the nearest .diff-file-box.
 * @param showAddedChanged - Whether to show the "added/changed" (diff_to) layer
 * @param showRemoved - Whether to show the "removed" (diff_from) layer
 */
export function setDiffLayers(container: Element | Document, showAddedChanged: boolean, showRemoved: boolean) {
  for (const diffTo of container.querySelectorAll<SVGElement>('.diff.diff_to')) {
    diffTo.style.display = showAddedChanged ? 'inline' : 'none';
  }

  for (const diffFrom of container.querySelectorAll<SVGElement>('.diff.diff_from')) {
    diffFrom.style.display = showRemoved ? 'inline' : 'none';
  }

  const showChanges = showAddedChanged || showRemoved;
  for (const backdrop of container.querySelectorAll<SVGElement>('.diff-backdrop')) {
    backdrop.style.display = showChanges ? 'inline' : 'none';
  }

  for (const el of container.querySelectorAll<SVGElement>('[data-original-color], [data-original-stroke], [data-original-fill]')) {
    const origColor = el.getAttribute('data-original-color');
    if (origColor) {
      el.setAttribute('color', showChanges ? el.getAttribute('data-desaturated-color') : origColor);
    }

    const origFill = el.getAttribute('data-original-fill');
    if (origFill) {
      el.setAttribute('fill', showChanges ? el.getAttribute('data-desaturated-fill') : origFill);
    }

    const origStroke = el.getAttribute('data-original-stroke');
    if (origStroke) {
      el.style.stroke = showChanges ? el.getAttribute('data-desaturated-stroke') : origStroke;
    }
  }
}

/**
 * Re-apply the current diff toggle state from the Pinia store to SVG elements.
 */
export function refreshDiffLayers(container: Element | Document) {
  const store = useDiffToggleStore();
  setDiffLayers(container, store.showAddedChanged, store.showRemoved);
}
